import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ProductCard from "../components/ProductCard";
// import { fetchProducts, searchProducts } from "../services/productService";
import {
  fetchProducts,
  fetchCategories,
} from "../services/productService";
import { useCart } from "../context/CartContext";

/* ─────────────────────────────────────────────
   FALLBACK DATA  (used when API is unavailable)
───────────────────────────────────────────── */
const FALLBACK_PRODUCTS = [
];
const SORT_OPTIONS = [
  { value: "default", label: "Featured" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "rating", label: "Top Rated" },
  { value: "newest", label: "Newest" },
];

/* ─────────────────────────────────────────────
   SKELETON CARD
───────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="skeleton-card">
    <div className="skeleton-img shimmer" />
    <div className="skeleton-body">
      <div className="skeleton-line shimmer" style={{ width: "55%", height: 11 }} />
      <div className="skeleton-line shimmer" style={{ width: "85%", height: 18, marginTop: 8 }} />
      <div className="skeleton-line shimmer" style={{ width: "40%", height: 11, marginTop: 10 }} />
      <div className="skeleton-line shimmer" style={{ width: "30%", height: 22, marginTop: 14 }} />
    </div>
    <div className="skeleton-footer shimmer" />
  </div>
);

/* ─────────────────────────────────────────────
   PRODUCTS PAGE
───────────────────────────────────────────── */
export default function Products() {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebounced] = useState("");
  const [activeCategory, setCategory] = useState("All");
  const [sortBy, setSortBy] = useState("default");
  const [cartCount, setCartCount] = useState(0);
  const [toast, setToast] = useState(null);
  const debounceRef = useRef(null);
  const { addItem } = useCart();
  const [categories, setCategories] = useState(["All"]);

  /* ── Fetch on mount ── */
  useEffect(() => {

    const loadData = async () => {

      try {

        setLoading(true);
        setError(null);

        // Fetch products
        const productData = await fetchProducts();
        console.log("All Products API:", productData);
        console.log("Is Array:", Array.isArray(productData));
        console.log("First Product:", productData?.[0]);
        setAllProducts(
          Array.isArray(productData)
            ? productData
            : []
        );

        // Fetch categories
        const categoryData = await fetchCategories();

        setCategories([
          "All",
          ...(Array.isArray(categoryData)
            ? categoryData
            : []),
        ]);
      } catch (error) {

        console.error(error);

        setError("Failed to load products");

      } finally {

        setLoading(false);

      }
    };

    loadData();

  }, []);

  /* ── Debounce search ── */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(searchQuery), 320);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  /* ── Filter + sort ── */
  const displayed = useMemo(() => {

    // IMPORTANT
    // create NEW array copy

    let list = [...allProducts];

    console.log("All Products:", allProducts);
    console.log("List:", list);

    // =========================
    // SEARCH
    // =========================

    if (debouncedQuery.trim()) {

      const q = debouncedQuery.toLowerCase();

      list = list.filter((p) =>
        p.name?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      );
    }

    // =========================
    // CATEGORY FILTER
    // =========================

    if (activeCategory !== "All") {

      list = list.filter(
        (p) => p.category === activeCategory
      );
    }

    // =========================
    // SORT
    // =========================

    switch (sortBy) {

      case "price_asc":

        list.sort(
          (a, b) => a.price - b.price
        );

        break;

      case "price_desc":

        list.sort(
          (a, b) => b.price - a.price
        );

        break;

      case "rating":

        list.sort(
          (a, b) => (b.rating || 0) - (a.rating || 0)
        );

        break;

      default:
        break;
    }

    return list;

  }, [
    allProducts,
    debouncedQuery,
    activeCategory,
    sortBy
  ]);
  /* ── Cart ── */
  // const handleAddToCart = useCallback((product) => {
  //   setCartCount(c => c + 1);
  //   addItem({
  //     id: product._id,
  //     name: product.name,
  //     price: product.price,
  //     emoji: product.emoji || "🌿",
  //     category: product.category || "",
  //   });
  //   setToast(`"${product.name}" added to cart!`);
  //   setTimeout(() => setToast(null), 2800);
  // }, []);

  const handleAddToCart = useCallback((product) => {
    addItem(product); // pass the full product object directly
    setToast(`"${product.name}" added to cart!`);
    setTimeout(() => setToast(null), 2800);
  }, [addItem]);

  /* ── Retry ── */
  const handleRetry = () => {
    setError(null);
    setAllProducts(FALLBACK_PRODUCTS);
  };

  /* ── Clear search ── */
  const clearSearch = () => { setSearchQuery(""); setCategory("All"); setSortBy("default"); };

  return (
    <>
      <style>{css}</style>

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}

      <div className="products-page">

        {/* ── PAGE HEADER ── */}
        <header className="products-header">
          <div className="products-header__bg" aria-hidden="true" />
          <div className="container">
            <span className="products-header__eyebrow">✦ Our Collection</span>
            <h1 className="products-header__title">Pure Ayurvedic Products</h1>
            <p className="products-header__sub">
              Sustainably sourced · Lab-tested · GMP Certified
            </p>
          </div>
        </header>

        <div className="container products-layout">

          {/* ── TOOLBAR ── */}
          <div className="toolbar">
            {/* Search */}
            <div className="search-wrap">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search products…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                aria-label="Search products"
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">✕</button>
              )}
            </div>

            {/* Sort */}
            <div className="sort-wrap">
              <select
                className="sort-select"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                aria-label="Sort products"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── CATEGORIES ── */}
          <div className="categories" role="list" aria-label="Product categories">
            {categories.map(cat => (
              <button
                key={cat}
                role="listitem"
                className={`cat-chip ${activeCategory === cat ? "cat-chip--active" : ""}`}
                onClick={() => setCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* ── RESULTS META ── */}
          {!loading && !error && (
            <div className="results-meta">
              <span>
                {displayed.length === 0
                  ? "No products found"
                  : `${displayed.length} product${displayed.length !== 1 ? "s" : ""}`}
                {/* {activeCategory !== "All" && ` in "${activeCategory}"`} */}
                {debouncedQuery && ` matching "${debouncedQuery}"`}
              </span>
              {(debouncedQuery || activeCategory !== "All") && (
                <button className="clear-btn" onClick={clearSearch}>Clear filters</button>
              )}
            </div>
          )}

          {/* ── LOADING ── */}
          {loading && (
            <div className="products-grid">
              {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {/* ── ERROR ── */}
          {error && (
            <div className="error-state">
              <span className="error-state__icon">🌿</span>
              <h3>Something went wrong</h3>
              <p>{error}</p>
              <button className="btn-primary" onClick={handleRetry}>Try Again</button>
            </div>
          )}

          {/* ── EMPTY STATE ── */}
          {!loading && !error && displayed.length === 0 && (
            <div className="empty-state">
              <span className="empty-state__icon">🔍</span>
              <h3>No products found</h3>
              <p>Try adjusting your search or category filter.</p>
              <button className="btn-primary" onClick={clearSearch}>Clear Filters</button>
            </div>
          )}

          {/* ── GRID ── */}
          {/* ── GRID ── */}
          {!loading && !error && displayed.length > 0 && (
            <div className="products-grid">
              {displayed.map((product, i) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onAddToCart={handleAddToCart}
                  style={{
                    "--delay": `${(i % 8) * 0.07}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');

/* ── VARS ── */
:root {
  --c-primary:       #2d6a4f;
  --c-primary-light: #40916c;
  --c-primary-dark:  #1b4332;
  --c-accent:        #d4a017;
  --c-bg:            #f9f6f0;
  --c-surface:       #ffffff;
  --c-text:          #1c2b22;
  --c-muted:         #5a7262;
  --c-border:        #d4e6d5;
  --c-error:         #c0392b;
  --f-display:       'Cormorant Garamond', Georgia, serif;
  --f-body:          'Jost', sans-serif;
  --radius:          14px;
  --shadow:          0 4px 24px rgba(27,67,50,.09);
  --shadow-lg:       0 12px 40px rgba(27,67,50,.15);
  --ease:            cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--f-body); background: var(--c-bg); color: var(--c-text); }

.container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* ── PAGE HEADER ── */
.products-header {
  position: relative; overflow: hidden;
  padding: 72px 0 52px; margin-bottom: 0;
  background: linear-gradient(135deg, var(--c-primary-dark) 0%, #1e4d35 60%, #163d2a 100%);
  text-align: center;
  padding:100px 0 50px 0;
}
.products-header__bg {
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 60% 80% at 80% 50%, rgba(64,145,108,.22), transparent 60%),
    radial-gradient(ellipse 40% 60% at 10% 80%, rgba(212,160,23,.10), transparent 55%);
}
.products-header__eyebrow {
  display: inline-block; font-size: .72rem; font-weight: 600;
  letter-spacing: .2em; text-transform: uppercase;
  color: var(--c-accent); background: rgba(212,160,23,.14);
  padding: 5px 16px; border-radius: 100px; margin-bottom: 16px;
}
.products-header__title {
  font-family: var(--f-display); font-size: clamp(2.2rem, 5vw, 3.5rem);
  font-weight: 600; color: #fff; line-height: 1.15; margin-bottom: 12px;
}
.products-header__sub {
  font-size: .9rem; color: rgba(255,255,255,.58);
  font-weight: 300; letter-spacing: .06em;
}

/* ── LAYOUT ── */
.products-layout { padding-top: 36px; padding-bottom: 72px; }

/* ── TOOLBAR ── */
.toolbar {
  display: flex; align-items: center; gap: 14px;
  flex-wrap: wrap; margin-bottom: 20px;
}

.search-wrap {
  flex: 1; min-width: 220px; position: relative;
  display: flex; align-items: center;
}
.search-icon { position: absolute; left: 14px; font-size: .95rem; pointer-events: none; }
.search-input {
  width: 100%; font-family: var(--f-body); font-size: .9rem;
  padding: 11px 40px 11px 40px;
  background: var(--c-surface); border: 1.5px solid var(--c-border);
  border-radius: 100px; outline: none; color: var(--c-text);
  transition: border-color .22s, box-shadow .22s;
}
.search-input:focus { border-color: var(--c-primary-light); box-shadow: 0 0 0 3px rgba(64,145,108,.15); }
.search-input::placeholder { color: var(--c-muted); }
.search-clear {
  position: absolute; right: 14px; background: none; border: none;
  cursor: pointer; font-size: .8rem; color: var(--c-muted);
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  transition: background .2s;
}
.search-clear:hover { background: var(--c-border); }

.sort-select {
  font-family: var(--f-body); font-size: .85rem; font-weight: 500;
  padding: 10px 36px 10px 16px; border-radius: 100px;
  border: 1.5px solid var(--c-border); background: var(--c-surface);
  color: var(--c-text); cursor: pointer; outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%235a7262' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 14px center;
  transition: border-color .22s;
}
.sort-select:focus { border-color: var(--c-primary-light); }

.cart-pill {
  display: flex; align-items: center; gap: 7px;
  font-family: var(--f-body); font-size: .82rem; font-weight: 600;
  padding: 10px 20px; border-radius: 100px; border: none;
  background: var(--c-primary); color: #fff; cursor: pointer;
  transition: background .22s, transform .22s;
  white-space: nowrap;
}
.cart-pill:hover { background: var(--c-primary-dark); transform: translateY(-1px); }
.cart-pill__count {
  background: var(--c-accent); color: #fff;
  font-size: .7rem; font-weight: 700;
  min-width: 20px; height: 20px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  padding: 0 5px;
}

/* ── CATEGORIES ── */
.categories {
  display: flex; gap: 9px; flex-wrap: wrap; margin-bottom: 28px;
}
.cat-chip {
  font-family: var(--f-body); font-size: .78rem; font-weight: 500;
  padding: 7px 18px; border-radius: 100px;
  border: 1.5px solid var(--c-border);
  background: var(--c-surface); color: var(--c-muted);
  cursor: pointer; transition: all .22s var(--ease);
  white-space: nowrap;
}
.cat-chip:hover { border-color: var(--c-primary-light); color: var(--c-primary); }
.cat-chip--active {
  background: var(--c-primary); color: #fff;
  border-color: var(--c-primary);
  display:none;
  box-shadow: 0 4px 14px rgba(45,106,79,.28);
}

/* ── RESULTS META ── */
.results-meta {
  display: flex; align-items: center; gap: 16px;
  font-size: .82rem; color: var(--c-muted); margin-bottom: 24px;
  flex-wrap: wrap;
}
.clear-btn {
  background: none; border: 1px solid var(--c-border);
  font-family: var(--f-body); font-size: .78rem; font-weight: 500;
  color: var(--c-primary); padding: 4px 14px; border-radius: 100px;
  cursor: pointer; transition: all .2s;
}
.clear-btn:hover { background: var(--c-primary); color: #fff; border-color: var(--c-primary); }

/* ── GRID ── */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
}

/* ══════════════════════════════
   PRODUCT CARD  (.pc-*)
══════════════════════════════ */
.pc {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  display: flex; flex-direction: column;
  position: relative; overflow: hidden;
  transition: transform .28s var(--ease), box-shadow .28s var(--ease), border-color .28s;
  animation: cardIn .5s var(--ease) both;
  animation-delay: var(--delay, 0s);
}
@keyframes cardIn { from { opacity:0; transform: translateY(20px); } to { opacity:1; transform: none; } }

.pc:hover { transform: translateY(-5px); box-shadow: var(--shadow-lg); border-color: var(--c-primary-light); }
.pc::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--c-primary), var(--c-primary-light));
  opacity: 0; transition: opacity .28s;
}
.pc:hover::before { opacity: 1; }
.pc--oos { opacity: .72; }

.pc-badge {
  position: absolute; top: 12px; left: 12px; z-index: 2;
  background: var(--c-primary); color: #fff;
  font-size: .62rem; font-weight: 700; letter-spacing: .08em;
  text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
}
.pc-discount {
  position: absolute; top: 12px; right: 12px; z-index: 2;
  background: #e53e3e; color: #fff;
  font-size: .62rem; font-weight: 700;
  padding: 3px 9px; border-radius: 100px;
}

/* Image */
.pc-img-wrap {
  width: 100%; aspect-ratio: 1 / 1; overflow: hidden;
  background: linear-gradient(135deg, #f0f7f2 0%, #e8f5ec 100%);
  position: relative;
  display: flex;
    justify-content: center;
    align-items: center;
}
.pc-img {
  height: 100%; 
  object-fit: cover;
  transition: transform .38s var(--ease);
  width: fit-content;
  mix-blend-mode: multiply;
}
.pc:hover .pc-img { transform: scale(1.06); }
.pc-img-fallback {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 4.5rem;
  transition: transform .38s var(--ease);
}
.pc:hover .pc-img-fallback { transform: scale(1.1) rotate(-4deg); }
.pc-oos-overlay {
  position: absolute; inset: 0;
  background: rgba(249,246,240,.72);
  display: flex; align-items: center; justify-content: center;
  font-size: .78rem; font-weight: 700; letter-spacing: .1em;
  text-transform: uppercase; color: var(--c-muted);
}

/* Body */
.pc-body { padding: 18px 18px 4px; flex: 1; }
.pc-category {
  display: block; font-size: .67rem; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--c-primary-light); margin-bottom: 5px;
}
.pc-name {
  font-family: var(--f-display); font-size: 1.15rem; font-weight: 600;
  color: var(--c-primary-dark); line-height: 1.25; margin-bottom: 9px;
}
.pc-rating { display: flex; align-items: center; gap: 5px; margin-bottom: 11px; }
.pc-star { font-size: .85rem; }
.pc-reviews { font-size: .74rem; color: var(--c-muted); }
.pc-price-row { display: flex; align-items: baseline; gap: 9px; }
.pc-price {font-size: 1rem; font-weight: 600; color: var(--c-primary-dark); }
.pc-original { font-size: .85rem; color: var(--c-muted); text-decoration: line-through; }

/* Footer */
.pc-footer {
  padding: 14px 18px 18px;
  display: flex; align-items: center; gap: 10px;
}
.pc-btn {
  flex: 1; font-family: var(--f-body); font-size: .76rem; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase;
  padding: 10px 16px; border-radius: 100px; border: none; cursor: pointer;
  background: var(--c-primary); color: #fff;
  transition: background .22s, transform .18s, box-shadow .22s;
}
.pc-btn:hover:not(:disabled) { background: var(--c-primary-dark); transform: scale(1.03); box-shadow: 0 4px 14px rgba(45,106,79,.28); }
.pc-btn:disabled { background: var(--c-border); color: var(--c-muted); cursor: not-allowed; }
.pc-btn--added { background: #276749 !important; }

.pc-wishlist {
  width: 38px; height: 38px; border-radius: 50%;
  border: 1.5px solid var(--c-border); background: var(--c-bg);
  font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--c-muted); transition: all .22s;
  flex-shrink: 0;
}
.pc-wishlist:hover { border-color: #e53e3e; color: #e53e3e; background: #fff5f5; }

/* ── SKELETON ── */
.skeleton-card {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); overflow: hidden;
}
.skeleton-img { width: 100%; aspect-ratio: 1/1; }
.skeleton-body { padding: 18px; }
.skeleton-line { border-radius: 6px; background: #e8ede9; }
.skeleton-footer { height: 52px; margin: 0 18px 18px; border-radius: 100px; background: #e8ede9; }
.shimmer {
  background: linear-gradient(90deg, #e8ede9 25%, #d4e0d6 50%, #e8ede9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── STATES ── */
.error-state, .empty-state {
  text-align: center; padding: 80px 24px;
  grid-column: 1 / -1;
}
.error-state__icon, .empty-state__icon { font-size: 3.5rem; display: block; margin-bottom: 16px; }
.error-state h3, .empty-state h3 {
  font-family: var(--f-display); font-size: 1.6rem; font-weight: 600;
  color: var(--c-primary-dark); margin-bottom: 10px;
}
.error-state p, .empty-state p { font-size: .95rem; color: var(--c-muted); margin-bottom: 24px; }
.btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--f-body); font-size: .82rem; font-weight: 600;
  letter-spacing: .07em; text-transform: uppercase;
  padding: 12px 28px; border-radius: 100px; border: none;
  background: var(--c-primary); color: #fff; cursor: pointer;
  transition: background .22s, transform .22s;
}
.btn-primary:hover { background: var(--c-primary-dark); transform: translateY(-1px); }

/* ── TOAST ── */
.toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  background: var(--c-primary-dark); color: #fff;
  font-family: var(--f-body); font-size: .85rem; font-weight: 500;
  padding: 12px 24px; border-radius: 100px;
  box-shadow: 0 8px 32px rgba(0,0,0,.22);
  z-index: 9999; white-space: nowrap;
  animation: toastIn .3s var(--ease);
}
@keyframes toastIn { from { opacity:0; transform: translateX(-50%) translateY(12px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }

/* ── RESPONSIVE ── */
@media (max-width: 640px) {
  .toolbar { flex-direction: column; align-items: stretch; }
  .cart-pill { align-self: flex-end; }
  .products-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
}
@media (max-width: 350px) {
  .products-grid { grid-template-columns: 1fr; }
}
`;
